import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Card } from "./ui/card";
import { CheckCircle2, ChevronRight, Loader2, Trophy, Upload, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { validaData, dataNoFuturo, dataPosterior } from "../lib/validators";
import MultiDocumentUploader from "./MultiDocumentUploader";
import ClienteAutocomplete from "./ClienteAutocomplete";

const TIPOS = [
  { id: "12_meses", label: "Contrato 12 meses", pct: 12 },
  { id: "mensal", label: "Contrato Mensal", pct: 12 },
  { id: "renovacao", label: "Renovação de Contrato", pct: 5 },
  { id: "venda", label: "Venda", pct: 3 },
];

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function WizardFechamento({ lead, onComplete, autoOpen = false, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(autoOpen);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [contratoBonusId, setContratoBonusId] = useState(null);

  // Passo 1
  const [clienteNome, setClienteNome] = useState(lead?.empresa || lead?.nome || "");
  const [clienteCnpj, setClienteCnpj] = useState("");
  const [clienteSelecionado, setClienteSelecionado] = useState(null);

  // Passo 2
  const [form, setForm] = useState({
    tipo: "12_meses",
    valor_primeira_fatura: 0,
    data_assinatura: new Date().toISOString().slice(0, 10),
    data_inicio: new Date().toISOString().slice(0, 10),
    data_termino: "",
    observacoes: "",
  });
  const [documentos, setDocumentos] = useState([]);

  // Passo 3
  const [equipamentos, setEquipamentos] = useState([{ nome: "", quantidade: 1, descricao: "", observacoes_logistica: "" }]);

  const pctSelecionado = TIPOS.find((t) => t.id === form.tipo)?.pct || 0;
  const valorBonus = (Number(form.valor_primeira_fatura) || 0) * pctSelecionado / 100;

  const reset = () => {
    setStep(1); setBusy(false); setContratoBonusId(null);
    setDocumentos([]);
    setClienteCnpj(""); setClienteSelecionado(null);
    setEquipamentos([{ nome: "", quantidade: 1, descricao: "", observacoes_logistica: "" }]);
  };

  const finalizarStep1 = async () => {
    if (!clienteNome.trim()) {
      toast.error("Informe o nome do cliente");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/ativar-cliente`, {
        cliente_nome: clienteNome,
        cliente_id: clienteSelecionado?.id || null,
      });
      toast.success("Cliente ativado!");
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao ativar");
    } finally { setBusy(false); }
  };

  const finalizarStep2 = async () => {
    if (!form.valor_primeira_fatura || form.valor_primeira_fatura <= 0) {
      toast.error("Informe o valor da primeira fatura"); return;
    }
    if (!form.data_assinatura || !form.data_inicio || !form.data_termino) {
      toast.error("Datas obrigatórias"); return;
    }
    // Bloco 3 — Validações de datas
    if (!validaData(form.data_assinatura) || !validaData(form.data_inicio) || !validaData(form.data_termino)) {
      toast.error("Data inválida (verifique formato e dia válido)"); return;
    }
    if (dataNoFuturo(form.data_assinatura)) {
      toast.error("Data de assinatura não pode ser futura"); return;
    }
    if (!dataPosterior(form.data_termino, form.data_inicio)) {
      toast.error("Data de término deve ser posterior à data de início"); return;
    }
    // AJUSTE 2 — anexos são opcionais. Sistema NÃO trava sem PDFs.
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("lead_id", lead.id);
      fd.append("cliente_nome", clienteNome);
      if (clienteSelecionado?.id) fd.append("cliente_id", clienteSelecionado.id);
      fd.append("tipo", form.tipo);
      fd.append("valor_primeira_fatura", String(form.valor_primeira_fatura));
      fd.append("data_assinatura", form.data_assinatura);
      fd.append("data_inicio", form.data_inicio);
      fd.append("data_termino", form.data_termino);
      if (form.observacoes) fd.append("observacoes", form.observacoes);
      // Multi-documentos (Iter57) — até 10 arquivos PDF/DOC/JPG/PNG/WEBP
      documentos.forEach((d) => {
        if (d.file) fd.append("documentos", d.file);
      });
      const { data } = await api.post("/comercial/contratos-bonus", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContratoBonusId(data.id);
      const pendenciaMsg = data.signature_status === "pending" ? " (assinaturas pendentes)" : "";
      toast.success(`Contrato criado! Bônus: ${fmtMoney(data.valor_bonus)}${pendenciaMsg}`);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar contrato");
    } finally { setBusy(false); }
  };

  const finalizarStep3 = async () => {
    if (!contratoBonusId) return;
    const validos = equipamentos.filter((e) => e.nome.trim());
    if (validos.length === 0) {
      toast.error("Adicione pelo menos 1 equipamento"); return;
    }
    setBusy(true);
    try {
      for (const eq of validos) {
        await api.post(`/comercial/contratos-bonus/${contratoBonusId}/equipamentos`, {
          nome: eq.nome,
          quantidade: Number(eq.quantidade) || 1,
          descricao: eq.descricao || null,
          observacoes_logistica: eq.observacoes_logistica || null,
        });
      }
      toast.success("Fechamento concluído! Financeiro e Logística foram notificados.");
      setOpen(false);
      reset();
      onComplete?.();
      // AJUSTE 1 — Redirecionamento pós-ativação para a tela do CONTRATO ATIVO
      // Diretor/Supervisão/Financeiro/Gerente → Financeiro Comercial (ContratoBônus + KPIs + Logística)
      // Vendedor → volta para o lead (já efetivado), pois não tem acesso à tela financeira
      const podeVerFinanceiro = ["diretor", "supervisao", "financeiro", "gerente"].includes(user?.role);
      if (podeVerFinanceiro) {
        navigate(`/app/financeiro-comercial?contrato=${contratoBonusId}`);
      } else if (lead?.id) {
        navigate(`/app/leads/${lead.id}?tab=pedidos`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar equipamentos");
    } finally { setBusy(false); }
  };

  const StepDot = ({ n, active, done }) => (
    <div className={`flex items-center gap-2 ${active ? "" : "opacity-50"}`}>
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
        style={{ background: done ? "#16a34a" : active ? "#1B3A6B" : "#94a3b8" }}
      >
        {done ? <CheckCircle2 size={16} /> : n}
      </div>
      <span className="text-sm font-medium">
        {n === 1 ? "Ativar Cliente" : n === 2 ? "Contrato + Proposta" : "Equipamentos"}
      </span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { reset(); onClose?.(); } }}>
      {!autoOpen && (
        <DialogTrigger asChild>
          <Button className="text-white" style={{ background: "#F97316" }} data-testid="wizard-fechamento-btn">
            <Trophy size={14} className="mr-2" /> Iniciar Fechamento
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-3xl" data-testid="wizard-fechamento-dialog">
        <DialogHeader>
          <DialogTitle>Wizard de Fechamento</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-3 mb-4 border-b pb-4">
          <StepDot n={1} active={step === 1} done={step > 1} />
          <ChevronRight size={16} className="text-zinc-400" />
          <StepDot n={2} active={step === 2} done={step > 2} />
          <ChevronRight size={16} className="text-zinc-400" />
          <StepDot n={3} active={step === 3} done={false} />
        </div>

        {step === 1 && (
          <div className="space-y-3" data-testid="step-1">
            <h3 className="font-bold">Passo 1 — Ativar Cliente</h3>
            <div>
              <Label>Buscar cliente cadastrado</Label>
              <ClienteAutocomplete
                value={clienteNome}
                onChange={(v) => { setClienteNome(v); if (clienteSelecionado) { setClienteSelecionado(null); setClienteCnpj(""); } }}
                onSelect={(c) => {
                  setClienteSelecionado(c);
                  setClienteNome(c.nome || "");
                  setClienteCnpj(c.cnpj_cpf || "");
                  toast.success(`Cliente selecionado: ${c.nome}`);
                }}
                placeholder="Digite o nome ou CNPJ do cliente..."
                testId="wiz-cliente-autocomplete"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Selecione um cliente da base ou digite um novo nome para cadastrar.
              </p>
            </div>
            {clienteSelecionado && (
              <div className="text-xs bg-emerald-50 px-3 py-2 rounded border border-emerald-200 flex items-center gap-2"
                   data-testid="wiz-cliente-selecionado">
                <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-emerald-900 truncate">{clienteSelecionado.nome}</div>
                  <div className="flex gap-3 mt-0.5">
                    {clienteCnpj && <span className="font-mono text-slate-700">{clienteCnpj}</span>}
                    {clienteSelecionado.tipo_cliente && (
                      <span className="uppercase text-violet-600 text-[10px] font-medium">
                        {clienteSelecionado.tipo_cliente}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setClienteSelecionado(null); setClienteCnpj(""); setClienteNome(""); }}
                  className="text-emerald-700 hover:text-red-600 text-xs"
                  data-testid="wiz-cliente-limpar"
                >
                  Trocar
                </button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Ao confirmar, o cliente ficará marcado como Ativo e a data será registrada.</p>
            <Button onClick={finalizarStep1} disabled={busy} className="text-white" style={{ background: "#1B3A6B" }} data-testid="wiz-step1-confirm">
              {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />} Ativar Cliente
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="step-2">
            <h3 className="font-bold">Passo 2 — Contrato e Proposta Assinados</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Tipo de negócio</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="wiz-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label} ({t.pct}% de bônus)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor da 1ª fatura (R$) *</Label>
                <Input data-testid="wiz-valor" type="number" value={form.valor_primeira_fatura} onChange={(e) => setForm({ ...form, valor_primeira_fatura: e.target.value })} />
              </div>
              <div className="flex items-end p-2 rounded bg-amber-50 border border-amber-200">
                <div className="text-sm">
                  <div className="text-xs text-amber-700">Bônus calculado ({pctSelecionado}%)</div>
                  <div className="text-xl font-bold" style={{ color: "#1B3A6B" }} data-testid="wiz-bonus-preview">{fmtMoney(valorBonus)}</div>
                </div>
              </div>
              <div>
                <Label>Data de assinatura *</Label>
                <Input type="date" value={form.data_assinatura} onChange={(e) => setForm({ ...form, data_assinatura: e.target.value })} />
              </div>
              <div>
                <Label>Data de início *</Label>
                <Input type="date" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Data de término *</Label>
                <Input data-testid="wiz-data-termino" type="date" value={form.data_termino} onChange={(e) => setForm({ ...form, data_termino: e.target.value })} />
              </div>
              <div className="col-span-2">
                <MultiDocumentUploader
                  label="Documentos do contrato"
                  sublabel="Opcional · PDF, DOC, JPG, PNG, WEBP · até 10 arquivos · 10 MB cada"
                  value={documentos}
                  onChange={setDocumentos}
                  maxFiles={10}
                />
                {documentos.length === 0 && (
                  <p className="text-[10px] text-amber-700 mt-1">
                    Pode anexar depois — sem trava operacional.
                  </p>
                )}
              </div>
              <div className="col-span-2">
                <Label>Observações</Label>
                <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
              </div>
            </div>
            <Button onClick={finalizarStep2} disabled={busy} className="w-full text-white" style={{ background: "#1B3A6B" }} data-testid="wiz-step2-confirm">
              {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />} Salvar contrato e prosseguir
            </Button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="step-3">
            <h3 className="font-bold">Passo 3 — Equipamentos do Contrato</h3>
            <p className="text-xs text-muted-foreground">Ao salvar, Financeiro e Logística serão notificados.</p>
            {equipamentos.map((eq, idx) => (
              <Card key={idx} className="p-3 space-y-2" data-testid={`wiz-eq-${idx}`}>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-8">
                    <Label className="text-xs">Equipamento</Label>
                    <Input value={eq.nome} onChange={(e) => {
                      const c = [...equipamentos]; c[idx].nome = e.target.value; setEquipamentos(c);
                    }} placeholder="Ex: Andaime suspenso" />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Quantidade</Label>
                    <Input type="number" min={1} value={eq.quantidade} onChange={(e) => {
                      const c = [...equipamentos]; c[idx].quantidade = e.target.value; setEquipamentos(c);
                    }} />
                  </div>
                  <div className="col-span-1 flex items-end">
                    {equipamentos.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => setEquipamentos(equipamentos.filter((_, i) => i !== idx))}>
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>
                <Input value={eq.descricao} onChange={(e) => {
                  const c = [...equipamentos]; c[idx].descricao = e.target.value; setEquipamentos(c);
                }} placeholder="Descrição técnica (opcional)" />
                <Input value={eq.observacoes_logistica} onChange={(e) => {
                  const c = [...equipamentos]; c[idx].observacoes_logistica = e.target.value; setEquipamentos(c);
                }} placeholder="Observações para Logística (opcional)" />
              </Card>
            ))}
            <Button variant="outline" onClick={() => setEquipamentos([...equipamentos, { nome: "", quantidade: 1, descricao: "", observacoes_logistica: "" }])} data-testid="wiz-add-eq">
              <Plus size={14} className="mr-2" /> Adicionar equipamento
            </Button>
            <Button onClick={finalizarStep3} disabled={busy} className="w-full text-white" style={{ background: "#16a34a" }} data-testid="wiz-step3-confirm">
              {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <CheckCircle2 size={14} className="mr-2" />} Concluir Fechamento
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
